var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
import React from "react";
import Foundation from "@microsoft/fast-components-foundation-react";
import { Badge, BadgeSize, Card, Paragraph, ParagraphSize, StealthButton, Typography, TypographySize, } from "@microsoft/fast-components-react-msft";
import { Button as BaseButton, Hypertext, } from "@microsoft/fast-components-react-base";
import { typeRamp } from "@microsoft/fast-components-styles-msft";
import { applyMaxLines } from "@microsoft/fast-jss-utilities";
import { More } from "@edge-web-ui/news-icons";
import { ContentCardLayout, } from "./content-card.props";
import { Attribution } from "../attribution";
import { isNil, memoize } from "lodash-es";
import { classNames } from "@microsoft/fast-web-utilities";
class ContentCard extends Foundation {
    constructor(props) {
        super(props);
        this.handledProps = {
            abstract: void 0,
            attribution: void 0,
            curatedBy: void 0,
            destination: void 0,
            icon: void 0,
            heading: void 0,
            headingMaxLines: void 0,
            height: void 0,
            image: void 0,
            imagePriority: void 0,
            kicker: void 0,
            layout: void 0,
            likeIcon: void 0,
            managedClasses: void 0,
            menuTrigger: void 0,
            saveIcon: void 0,
            timestamp: void 0,
            width: void 0,
        };
        /**
         * Handles the hover event of the kicker
         * Updates the state of the component to notify when the kicker region has been hovered.
         * This updates a class on the root component which allows us to modify styles higher up the DOM tree
         */
        this.handleSecondaryActionHover = () => {
            this.setState({
                hideHeadingHover: !this.state.hideHeadingHover,
            });
        };
        /**
         * Handle the close button click and keyboard events
         */
        this.handleMenuTriggerClick = (e) => {
            if (!isNil(this.props.menuTrigger) &&
                typeof this.props.menuTrigger.onInvoke === "function") {
                this.props.menuTrigger.onInvoke(e);
            }
        };
        this.state = {
            hideHeadingHover: false,
        };
    }
    /**
     * Renders the component
     */
    render() {
        return (React.createElement(Card, Object.assign({}, this.generateAttributes(), { className: this.generateClassNames(), tag: this.props.tag }),
            React.createElement("div", { className: classNames(this.props.managedClasses.contentCard_positioningRegion) },
                this.renderImage(),
                this.renderContentArea()),
            this.renderAttributionRegion()));
    }
    /**
     * Generates class names
     */
    generateClassNames() {
        const { contentCard, contentCard__imagePriority, contentCard__hideHeadingHover, } = this.props.managedClasses;
        const isImagePriority = this.props.imagePriority ||
            (this.props.layout === ContentCardLayout._2x_2y && !!this.props.image);
        return super.generateClassNames(classNames(contentCard, [this.getClassNameByLayout(), !!this.props.layout], [contentCard__imagePriority, isImagePriority], [contentCard__hideHeadingHover, this.state.hideHeadingHover]));
    }
    generateAttributes() {
        const attributes = Object.assign({}, this.unhandledProps());
        return Object.assign({}, attributes, { style: Object.assign({}, attributes.style, { height: this.props.height, width: this.props.width }) });
    }
    getClassNameByLayout() {
        const { contentCard__1x_1y, contentCard__1x_2y, contentCard__1x_3y, contentCard__2x_2y, } = this.props.managedClasses;
        switch (this.props.layout) {
            case ContentCardLayout._1x_2y:
                return contentCard__1x_2y;
            case ContentCardLayout._1x_3y:
                return contentCard__1x_3y;
            case ContentCardLayout._2x_2y:
                return contentCard__2x_2y;
            case ContentCardLayout._1x_1y:
            default:
                return contentCard__1x_1y;
        }
    }
    /**
     * Render image based on props
     */
    renderImage() {
        const { contentCard_imageRegion, contentCard_obscuredRegion, contentCard_image, } = this.props.managedClasses;
        if (typeof this.props.image === "function") {
            return (React.createElement("div", { className: classNames(contentCard_imageRegion, contentCard_obscuredRegion) },
                this.props.image(classNames(contentCard_image)),
                this.props.layout !== ContentCardLayout._2x_2y
                    ? this.generateIcon()
                    : null));
        }
    }
    /**
     * Render content area based on props
     */
    renderContentArea() {
        return (React.createElement("div", { className: classNames(this.props.managedClasses.contentCard_contentRegion) },
            this.renderKicker(),
            this.renderCuratedBy(),
            this.renderHeading(),
            this.renderAbstract()));
    }
    /**
     * Render the article title with destination
     */
    renderHeading() {
        if (!this.props.heading) {
            return;
        }
        const _a = this.getHeadingProps(), { children, jssStyleSheet } = _a, headingProps = __rest(_a, ["children", "jssStyleSheet"]);
        const { contentCard_heading, contentCard_obscuredRegion, contentCard_headingContent, } = this.props.managedClasses;
        return (React.createElement(Hypertext, Object.assign({}, this.props.destination, { className: classNames(contentCard_heading, contentCard_obscuredRegion) }),
            React.createElement(Typography, Object.assign({}, headingProps, { jssStyleSheet: ContentCard.generateHeadingMaxlines(headingProps.size, this.props.headingMaxLines), className: classNames(contentCard_headingContent) }),
                children,
                this.props.layout === ContentCardLayout._2x_2y
                    ? this.generateIcon()
                    : null)));
    }
    /**
     * Render abstract based on props
     */
    renderAbstract() {
        if (!this.props.abstract ||
            this.props.imagePriority ||
            (this.props.layout === ContentCardLayout._1x_2y && this.props.image)) {
            return;
        }
        const { contentCard_abstract, contentCard_obscuredRegion, } = this.props.managedClasses;
        return (React.createElement(Paragraph, Object.assign({}, this.getAbstractProps(), { className: classNames(contentCard_abstract, contentCard_obscuredRegion) })));
    }
    /**
     * Render kicker badge based on props
     */
    renderKicker() {
        if (!this.props.kicker) {
            return;
        }
        const _a = this.props.kicker, { children } = _a, kickerProps = __rest(_a, ["children"]);
        const { contentCard_kicker, contentCard_kickerContent, } = this.props.managedClasses;
        return (React.createElement(BaseButton, Object.assign({}, kickerProps, { className: classNames(contentCard_kicker), onMouseEnter: this.handleSecondaryActionHover, onMouseLeave: this.handleSecondaryActionHover }),
            React.createElement(Badge, { className: classNames(contentCard_kickerContent), filled: false, size: BadgeSize.small }, children)));
    }
    /**
     * Renders the "curated by" logo based on props
     */
    renderCuratedBy() {
        if (typeof this.props.curatedBy === "function") {
            return this.props.curatedBy(this.props.managedClasses.contentCard_curatedBy);
        }
    }
    /**
     * Renders the article attribution
     */
    renderAttributionRegion() {
        return (React.createElement("div", { className: classNames(this.props.managedClasses.contentCard_attributionRegion) },
            this.renderAttribution(),
            this.renderTimestamp(),
            this.renderActionRegion()));
    }
    renderAttribution() {
        if (this.props.attribution) {
            return (React.createElement(Attribution, Object.assign({}, this.props.attribution, { className: classNames(this.props.managedClasses.contentCard_attribution) })));
        }
    }
    /**
     * Renders the timestamp
     */
    renderTimestamp() {
        if (this.props.timestamp) {
            return (React.createElement("span", { className: classNames(this.props.managedClasses.contentCard_timestamp) },
                this.props.attribution ? React.createElement("span", null, "\u2022") : null,
                React.createElement("span", null, this.props.timestamp)));
        }
    }
    renderActionRegion() {
        if (!this.props.saveIcon && !this.props.likeIcon && !this.props.menuTrigger) {
            return;
        }
        const { contentCard_actionRegion, contentCard_actionIcon, contentCard_saveIcon, // @deprecated
        contentCard_likeIcon, } = this.props.managedClasses;
        const saveIcon = typeof this.props.saveIcon === "function" &&
            this.props.saveIcon(classNames(contentCard_actionIcon, contentCard_saveIcon));
        const likeIcon = typeof this.props.likeIcon === "function" &&
            this.props.likeIcon(classNames(contentCard_actionIcon, contentCard_likeIcon));
        return (React.createElement("div", { className: classNames(contentCard_actionRegion), onMouseEnter: this.handleSecondaryActionHover, onMouseLeave: this.handleSecondaryActionHover },
            saveIcon,
            likeIcon,
            this.renderMenuTrigger()));
    }
    /**
     * Render the menu trigger
     */
    renderMenuTrigger() {
        if (this.props.menuTrigger) {
            // Destructure the close button prop object so we can pass "unhandled" props
            const _a = this.props.menuTrigger, { label, onInvoke } = _a, props = __rest(_a, ["label", "onInvoke"]);
            const { contentCard_menuGlyph, contentCard_actionIcon, contentCard_actionIcon__trigger, contentCard_menuTrigger, } = this.props.managedClasses;
            return (React.createElement(StealthButton, Object.assign({}, props, { "aria-label": label, className: classNames(contentCard_actionIcon, contentCard_actionIcon__trigger, contentCard_menuTrigger), onClick: this.handleMenuTriggerClick }),
                React.createElement(More, { className: classNames(contentCard_menuGlyph) })));
        }
    }
    /**
     * Generates icon based on props
     */
    generateIcon() {
        if (typeof this.props.icon === "function") {
            const { contentCard_icon, contentCard_iconWrapper, } = this.props.managedClasses;
            return (React.createElement("span", { className: classNames(contentCard_iconWrapper) }, this.props.icon(classNames(contentCard_icon))));
        }
    }
    /**
     * Gets props for the heading and sets default props if needed
     */
    getHeadingProps() {
        const defaultHeadingSize = this.props.layout !== ContentCardLayout._1x_1y
            ? TypographySize._5
            : this.props.image
                ? TypographySize._7
                : TypographySize._6;
        return Object.assign({}, { size: defaultHeadingSize }, this.props.heading);
    }
    /**
     * Gets props for the abstract and sets default props if needed
     */
    getAbstractProps() {
        return Object.assign({}, { size: ParagraphSize._3 }, this.props.abstract);
    }
}
ContentCard.defaultProps = {
    height: "163px",
    width: "300px",
    layout: ContentCardLayout._1x_1y,
    managedClasses: {},
    headingMaxLines: 3,
};
ContentCard.displayName = "ContentCard";
/**
 * Memoized function to get the heading stylesheet to avoid profileration of per-instance inline stylesheets.
 */
ContentCard.generateHeadingMaxlines = memoize((headingSize, maxLines) => ({
    typography: Object.assign({}, applyMaxLines(maxLines, typeRamp[`t${headingSize}`].lineHeight), { "white-space": "normal", 
        // apply webkit line clamp support for ellipsis in browsers which support it
        display: "-webkit-box", "-webkit-box-orient": "vertical", "-webkit-line-clamp": maxLines }),
}), (...args) => args.join());
export default ContentCard;
export * from "./content-card.props";
